import React, { useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useBooks } from '@/hooks/useBooks';
import { formatDate } from '@/utils/formatters';
import { Search } from 'lucide-react';

const AuthorBooks: React.FC = () => {
  const { profile } = useAuth();
  const { data: books, isLoading } = useBooks(profile?.id);
  const [searchQuery, setSearchQuery] = useState('');

  // Group books by title and combine their formats
  const groupedBooks = React.useMemo(() => {
    if (!books) return [];
    
    const grouped: Record<string, any> = {};
    books.forEach((book) => {
      if (!grouped[book.title]) {
        grouped[book.title] = {
          ...book,
          formats: [book.format || 'Unknown'],
          isbns: [book.isbn],
        };
      } else {
        if (book.format && !grouped[book.title].formats.includes(book.format)) {
          grouped[book.title].formats.push(book.format);
        }
        if (book.isbn && !grouped[book.title].isbns.includes(book.isbn)) {
          grouped[book.title].isbns.push(book.isbn);
        }
      }
    });
    
    return Object.values(grouped);
  }, [books]);

  // Filter books based on search query
  const filteredBooks = React.useMemo(() => {
    if (!searchQuery) return groupedBooks;
    
    return groupedBooks.filter((book) =>
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.isbns.some((isbn: string) => isbn.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [groupedBooks, searchQuery]);

  if (isLoading) {
    return (
      <MainLayout allowedRoles={['author']}>
        <div className="flex justify-center p-8">
          <p>Loading your books...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout allowedRoles={['author']}>
      <PageHeader
        title="My Books"
        description="View and manage your published books"
      />
      
      <Card>
        <CardHeader>
          <CardTitle>Published Books</CardTitle>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search books by title or ISBN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredBooks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {searchQuery ? 'No books found matching your search.' : 'No books published yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-medium">Title</th>
                    <th className="text-left p-4 font-medium">Formats</th>
                    <th className="text-left p-4 font-medium">ISBNs</th>
                    <th className="text-left p-4 font-medium">Publication Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBooks.map((book) => (
                    <tr key={book.id} className="border-b hover:bg-muted/50">
                      <td className="p-4">
                        <div className="font-medium">{book.title}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {book.formats.map((format: string) => (
                            <Badge key={format} variant="secondary" className="text-xs">
                              {format}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="space-y-1">
                          {book.isbns.map((isbn: string) => (
                            <div key={isbn} className="text-sm font-mono">
                              {isbn}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm">{formatDate(book.publishDate)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default AuthorBooks;